import React from "react";
import { C } from "../lib/colors.js";
import { DRAWER_TAB_Z } from "../lib/drawerStack.js";
import { RAIL_FOOTER, RAIL_SECTIONS, RAIL_TOOLS, RAIL_W } from "../lib/navRail.js";
import { surface, ui } from "../lib/telemetry/instrument.js";
import {
  IconPlus,
  IconAccount,
  IconChat,
  IconForecasts,
  IconQuestion,
  IconReport,
  IconSettings,
  IconSignals,
  IconTarget,
} from "./ui/Icons.jsx";
import { product as brandProduct } from "../branding.js";

// The navigation rail: a permanent column on the far left. Top: the Sayer wordmark, then one
// labelled item per section (Forecasts and Chat toggle the panel beside the rail), then Track
// record. Foot: Settings and Account. Pure props; App owns the state.

const ICONS = {
  new: IconPlus,
  forecasts: IconForecasts,
  chat: IconChat,
  question: IconQuestion,
  report: IconReport,
  signals: IconSignals,
  "track-record": IconTarget,
  settings: IconSettings,
  account: IconAccount,
};

/** Each section's drawer key, for the outside-click guards that ignore drawer toggles. */
const DRAWER_KEY = {
  forecasts: "log",
  chat: "chat",
  question: "contract",
  report: "report",
  signals: "tsd",
};

// Hover, focus and the selected state need real CSS (pseudo-classes), so the rail carries its
// own small stylesheet. Every colour is a theme token.
const RAIL_CSS = `
.sy-rail-item{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;width:64px;padding:8px 0 7px;border:0;border-radius:8px;background:transparent;color:var(--c-gray);cursor:pointer;flex-shrink:0;font:inherit;transition:background .12s ease,color .12s ease;}
.sy-rail-item svg{width:20px;height:20px;color:var(--c-grayL);transition:color .12s ease;}
.sy-rail-item:hover{background:var(--c-rowInactive);color:var(--c-white);}
.sy-rail-item:hover svg{color:var(--c-white);}
.sy-rail-item[data-active]{background:transparent;color:var(--c-white);box-shadow:inset 2px 0 0 var(--c-accent);}
.sy-rail-item[data-active] svg{color:var(--c-white);}
.sy-rail-item[aria-disabled="true"]{opacity:.25;cursor:default;}
.sy-rail-item[aria-disabled="true"]:hover{background:transparent;color:var(--c-gray);}
.sy-rail-item[aria-disabled="true"]:hover svg{color:var(--c-grayL);}
.sy-rail-item:focus-visible{outline:2px solid var(--c-accent);outline-offset:-2px;}
.sy-rail-item[data-flash] svg{color:var(--c-accent);animation:tourFlash .9s steps(1,end) infinite;}
.sy-rail-label{font-size:11px;font-weight:500;line-height:14px;letter-spacing:0;white-space:nowrap;}
.sy-rail-badge{position:absolute;top:4px;left:calc(50% + 6px);min-width:16px;height:16px;padding:0 4px;box-sizing:border-box;border-radius:8px;background:var(--c-accent);color:var(--c-btnText);font-size:11px;font-weight:600;line-height:16px;text-align:center;pointer-events:none;}
.sy-rail-mark{border:0;background:transparent;font:inherit;cursor:default;width:100%;display:flex;flex-direction:column;align-items:center;gap:5px;padding:14px 0 12px;user-select:none;}
button.sy-rail-mark{cursor:pointer;}
button.sy-rail-mark:focus-visible{outline:2px solid var(--c-accent);outline-offset:-2px;border-radius:8px;}
.sy-rail-mark span{font-size:13px;font-weight:600;color:var(--c-white);letter-spacing:0;line-height:1;}
`;

/**
 * One rail control: a real button (keyboard-reachable) whose visible name sits under its icon.
 * A disabled item says why in its tooltip.
 */
function RailItem({
  id,
  label,
  onActivate,
  active = false,
  // Announced as a toggle only where there is a state to report.
  pressed,
  disabled = false,
  disabledReason,
  flash = false,
  blink = false,
  badge = 0,
  // One word per line, for a label too wide for the rail.
  stacked = false,
  "data-ui": uiId,
  "data-ui-key": uiKey,
  ...rest
}) {
  const Icon = ICONS[id];
  return (
    <button
      type="button"
      className="sy-rail-item"
      aria-label={label}
      aria-pressed={pressed}
      aria-disabled={disabled || undefined}
      title={disabled ? disabledReason : undefined}
      data-active={active ? "" : undefined}
      data-flash={flash ? "" : undefined}
      onClick={(e) => {
        if (!disabled) onActivate?.(e);
      }}
      {...rest}
      {...ui(uiId, uiKey)}
    >
      {/* The same three-pulse blink the classic Question tab gives a freshly updated question. */}
      <span className={blink ? "contract-blink" : undefined} style={{ lineHeight: 0 }}>
        {Icon ? <Icon /> : null}
      </span>
      <span className="sy-rail-label">
        {stacked
          ? label.split(" ").map((w) => (
              <span key={w} style={{ display: "block" }}>
                {w}
              </span>
            ))
          : label}
      </span>
      {badge > 0 && (
        <span
          className="sy-rail-badge sy-num"
          title={`${badge} watchlist alert${badge === 1 ? "" : "s"}`}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

/** The Sayer mark and wordmark at the top of the rail. */
function RailWordmark({ onHome }) {
  const Tag = onHome ? "button" : "div";
  return (
    <Tag
      className="sy-rail-mark"
      {...(onHome
        ? {
            type: "button",
            onClick: onHome,
            title: "Show or hide the side panel",
            "aria-label": `${brandProduct.prose}: show or hide the side panel`,
            ...ui("nav-rail.toggle-panel"),
          }
        : { role: "img", "aria-label": brandProduct.prose })}
    >
      <div
        aria-hidden="true"
        style={{
          width: 22,
          height: 22,
          background: C.accent,
          WebkitMaskImage: "url(/logo.svg)",
          maskImage: "url(/logo.svg)",
          WebkitMaskSize: "contain",
          maskSize: "contain",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskPosition: "center",
        }}
      />
      <span aria-hidden="true">{brandProduct.prose}</span>
    </Tag>
  );
}

// Over the report and preprocess overlays (400), under the feedback form (600) and the settings /
// account / track record panels (550) the lifted controls open.
const RAIL_LIFTED_Z = 450;

export default function NavRail({
  // The start page (the Ask hero): clears the open forecast.
  onNewQuestion,
  activeSection,
  onSelectSection,
  onToggleCollapse,
  alertCount = 0,
  // The post-tour "send feedback" note flashes Account, where Send feedback lives.
  bugFlash,
  settingsOpen,
  onSettingsToggle,
  accountOpen,
  onAccountToggle,
  // The new-question page is open (no forecast showing).
  newQuestionActive = false,
  trackRecordOpen = false,
  onTrackRecordToggle,
  // The section the tour is flashing, if any (useRailTourFlash).
  flashSection = null,
  // The section to blink once, as the classic Question tab does on a new question.
  blinkSection = null,
  // Panel sections that have nothing to show yet.
  sectionDisabled = {},
  liftedOverOverlay = false,
}) {
  const renderSection = (s) => {
    const active = activeSection === s.id;
    return (
      <RailItem
        key={s.id}
        id={s.id}
        label={s.label}
        active={active}
        pressed={active}
        disabled={!!sectionDisabled[s.id]}
        disabledReason={sectionDisabled[s.id] ? "Ask a question first" : s.disabledLabel}
        flash={flashSection === s.id}
        blink={blinkSection === s.id}
        badge={s.id === "forecasts" ? alertCount : 0}
        onActivate={() => onSelectSection(s.id)}
        data-drawer-toggle={DRAWER_KEY[s.id]}
        data-nav-rail-item={s.id}
        {...ui("nav-rail.section", s.id)}
      />
    );
  };
  const footer = {
    "track-record": {
      active: trackRecordOpen,
      onActivate: onTrackRecordToggle,
      extra: { "data-track-record-toggle": "", ...ui("nav-rail.track-record") },
    },
    settings: {
      active: settingsOpen,
      onActivate: onSettingsToggle,
      extra: {
        "data-settings-toggle": "",
        "data-tour-spotlight": "settings-gear",
        ...ui("nav-rail.settings"),
      },
    },
    account: {
      active: accountOpen,
      onActivate: onAccountToggle,
      flash: bugFlash,
      extra: {
        "data-account-toggle": "",
        "data-tour-spotlight": "bug-icon",
        ...ui("nav-rail.account"),
      },
    },
  };
  const renderTool = (f) => {
    const cfg = footer[f.id];
    return (
      <RailItem
        key={f.id}
        id={f.id}
        label={f.label}
        stacked={!!f.stacked}
        active={!!cfg.active}
        pressed={!!cfg.active}
        flash={!!cfg.flash}
        onActivate={cfg.onActivate}
        {...cfg.extra}
      />
    );
  };
  return (
    <nav
      aria-label="Sections"
      data-nav-rail=""
      {...surface("nav-rail")}
      {...ui("nav-rail.toggle-panel")}
      // A click on the rail's empty space toggles the panel: collapse it, or bring back the
      // section that was last open — as the classic side tab's whole strip toggles its drawer.
      onClick={(e) => {
        if (!e.target.closest("button")) onToggleCollapse();
      }}
      style={{
        width: RAIL_W - 1, // + the 1px border
        flexShrink: 0,
        background: C.sidebarBg,
        borderRight: `1px solid ${C.edge}`,
        position: "relative",
        // Lifted over the report / preprocess overlays (z400) so the foot controls stay
        // full-strength and clickable; 450 stays under their own panels (550, 600). Lifted, the
        // rest of the rail goes inert, so a click on it falls through to the overlay's backdrop
        // and closes it, as a click outside the panel always has.
        zIndex: liftedOverOverlay ? RAIL_LIFTED_Z : DRAWER_TAB_Z,
        pointerEvents: liftedOverOverlay ? "none" : "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "0 0 8px",
        gap: 2,
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none",
      }}
    >
      <style>{RAIL_CSS}</style>
      <RailWordmark onHome={onToggleCollapse} />
      {/* Lifted, the sections are inert too: out of the tab order and unclickable by keyboard,
          as they already are to the pointer. `contents` keeps the rail's layout as it was. */}
      <div style={{ display: "contents" }} inert={liftedOverOverlay || undefined}>
        {onNewQuestion && (
          <RailItem
            id="new"
            label="New Question"
            stacked
            active={newQuestionActive}
            onActivate={onNewQuestion}
            data-ui="nav-rail.section"
            data-ui-key="new"
          />
        )}
        {RAIL_SECTIONS.filter((s) => s.inRail !== false).map((s) => renderSection(s))}
      </div>
      {/* Outside the inert sections, as the foot is: they stay usable while lifted. */}
      <div style={{ display: "contents", pointerEvents: "auto" }}>{RAIL_TOOLS.map(renderTool)}</div>
      <div style={{ flex: 1, minHeight: 12 }} />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          pointerEvents: "auto",
        }}
      >
        {RAIL_FOOTER.map(renderTool)}
      </div>
    </nav>
  );
}

/** The rail panel's heading: the section name, sentence case. */
export function RailPanelHeading({ label, actions = null, children = null }) {
  // `children` replaces the name (the Forecasts search box); `actions` sit at the right.
  return (
    <div
      style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "14px 16px 12px",
        borderBottom: `1px solid ${C.border}`,
        color: C.white,
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: 0,
        lineHeight: "20px",
        whiteSpace: "nowrap",
        userSelect: "none",
      }}
    >
      {children ?? <span style={{ flex: 1, minWidth: 0 }}>{label}</span>}
      {actions}
    </div>
  );
}
